import {Base} from "./base";
import {pick} from "../lib/util";

class BookService extends Base {
  constructor() {
    super();
  }

  /**
   * 医院列表
   * @returns {Promise<Array>}
   */
  async hosList() {
    const {code, list = []} = await this.$http('nethos.book.hos.area.list', {}, {}, {noloading: true});
    return code === '0' ? list : [];
  }

  /**
   * 某些情况下获取第一个医院为默认院内服务的医院
   * @param defaultIndex
   * @returns {Promise<*|{}>}
   */
  async getDefaultBookHos(defaultIndex = 0) {
    const list = await this.hosList();
    return list[defaultIndex] || {}
  }

  /**
   * 根据bookHosId获得hos
   * @param bookHosId
   * @returns {Promise<null|{}>}
   */
  async getBookHosByBookHosId(bookHosId) {
    const list = await this.hosList();
    return list.find(r => r.bookHosId === bookHosId) || {}
  }

  /**
   * 根据platHosId获得hos
   * @param platHosId
   * @returns {Promise<null|{}>}
   */
  async getBookHosByPlatHosId(platHosId) {
    const list = await this.hosList();
    return list.find(r => r.platHosId === platHosId) || {}
  }

  /**
   * 科室列表
   * @param bookHosId
   * @param {"REGISTER_BOOK"} registerType
   * @returns {Promise<Array>}
   */
  async deptList(bookHosId, registerType) {
    const {code, list = []} = await this.$http('nethos.book.dept.list.scheme.state', {bookHosId, registerType})
    return code === '0' ? list : [];
  }

  /**
   * 医院详情
   * @param id
   * @returns {Promise<*|{}>}
   */
  async hosDetail(id) {
    const list = await this.hosList();
    return list.find(r => r.bookHosId === id) || {};
  }

  /**
   * 医生详情
   * @param bookDocId
   * @returns {Promise<void>}
   */
  async docInfo(bookDocId) {
    const {code, obj = {}} = await this.$http('nethos.book.doc.info', {
      bookDocId
    });
    return code === '0' ? obj : {};
  }

  /**
   * 医生排班
   * @param bookDocId
   * @param schemeType
   * @param bookDeptId 本院专家
   * @returns {Promise<{}>}
   */
  async docScheme(bookDocId, schemeType, bookDeptId) {
    let meta = {};
    if (bookDeptId) meta = {bookDeptId};
    const {code, obj = {}} = await this.$http('nethos.book.doc.scheme.list', {
      bookDocId,
      schemeType,
      ...meta
    });
    return code === '0' ? obj : {};
  }

  /**
   * 本院专家医生排班
   * @param bookDocId
   * @param bookDeptId 本院专家
   * @param schemeType
   * @returns {Promise<{}>}
   */
  async docExpertScheme(bookDocId, bookDeptId, schemeType) {
    let meta = {};
    if (bookDeptId) meta = {bookDeptId};
    const {code, obj = {}} = await this.$http('nethos.book.doc.expert.scheme.list', {
      bookDocId,
      schemeType,
      ...meta
    });
    return code === '0' ? obj : {};
  }

  /**
   * 搜索医院、科室、医生
   * @param search
   * @param bookHosId
   * @returns {Promise<void>}
   */
  async search(search, bookHosId, schemeType) {
    const {code, list} = await this.$http('nethos.book.doc.list.search', {search, bookHosId, schemeType});
    return code === '0' ? list : [];
  }

  /**
   * 科室排班
   * @param bookDeptId
   * @param date
   * @param schemeType
   * @returns {Promise<void>}
   */
  async scheme({bookDeptId, date, schemeType}) {
    const {code, list = []} = await this.$http('nethos.book.doc.list.scheme.list', {
      bookDeptId, date, schemeType
    });
    return code === '0' ? list : [];
  }

  /**
   * 科室排班V2 3点到4点半
   * @param bookDeptId
   * @param date
   * @returns {Promise<void>}
   */
  async schemeV2({bookDeptId, date}) {
    // nethos.book.doc.list.tom.scheme.list
    const {code, list = []} = await this.$http('nethos.book.doc.list.tom.scheme.list', {
      bookDeptId, date
    });
    return code === '0' ? list : [];
  }

  /**
   * 号源列表
   * @param bookSchemeId
   * @returns {Promise<*[]>}
   */
  async numList(bookSchemeId) {
    const {list = []} = await this.$http('nethos.book.num.list', {bookSchemeId})
    return list;
  }


  /**
   * 获取医生列表v2
   * @param bookDeptId
   * @param schemeType
   * @returns {Promise<void>}
   */
  async listv2(bookDeptId, schemeType = null) {
    const {list = []} = await this.$http('nethos.book.doc.list.v2', {bookDeptId, schemeType});
    return list;
  }

  /**
   * 本院专家
   * @param bookDeptId
   * @param schemeType
   * @returns {Promise<*[]>}
   */
  async listv3(bookDeptId, schemeType) {
    const {list = []} = await this.$http('nethos.book.expert.doc.list', {bookDeptId, schemeType});
    return list;
  }

  /**
   * 获取医生列表包含排班信息
   * @param bookDeptId
   * @returns {Promise<void>}
   */
  async listState(bookDeptId) {
    const {obj = []} = await this.$http('nethos.book.doc.list.scheme.state', {bookDeptId});
    return obj;
  }

  /**
   * 预约挂号
   * @param bookNumId
   * @param bookHosId
   * @param compatId
   * @param mdtDeptList
   * @returns {Promise<void>}
   */
  async book({bookNumId, bookHosId, compatId, mdtDeptList}) {
    const {obj = {}} = await this.$http('nethos.book.order.register', {
      bookNumId, bookHosId, compatId, mdtDeptList
    })
    return obj;
  }


  /**
   * 当天挂号
   * @param bookSchemeId
   * @param bookHosId
   * @param compatId
   * @returns {Promise<void>}
   */
  async today({bookSchemeId, bookHosId, compatId}) {
    const {code, obj = {}} = await this.$http('nethos.book.z2order.register', {bookSchemeId, bookHosId, compatId});
    return code === '0' ? obj : {};
  }

  /**
   * HALO当天挂号
   * @param bookSchemeId
   * @param bookHosId
   * @param compatId
   * @param bookNumId
   * @param registeType
   * @returns {Promise<void>}
   */
  async haloToday({bookSchemeId, bookNumId, bookHosId, compatId, registeType}) {
    const {code, obj = {}} = await this.$http('nethos.book.z2order.halo.register', {
      bookSchemeId,
      bookNumId,
      bookHosId,
      compatId,
      registeType
    });
    return code === '0' ? obj : {};
  }

  async pay({orderId, openid, registeType = 2, returnUrl}) {
    const {code, obj} = await this.$http("nethos.book.order.pay.v3", {
      orderId, openid, registeType, returnUrl
    });
    return code === '0' ? obj : "";
  }

  async payV4({orderId, openid, registeType = 2, returnUrl}) {
    const {code, obj} = await this.$http("nethos.book.order.pay.v4", {
      orderId, openid, registeType, returnUrl
    });
    return code === '0' ? obj : "";
  }

  /**
   * 预约记录详情
   * @param orderId
   * @returns {Promise<*>}
   */
  async orderDetail(orderId) {
    const {code, obj = {}} = await this.$http('nethos.book.order.detail', {
      orderId
    });
    return code === '0' ? obj : {}
  }


  /**
   * 诊前录入
   * @param ddid
   * @param name
   * @param binganhao
   * @param qhmm
   * @param zhusu
   * @param guominshi
   * @param jiazushi
   * @param jiwangshi
   * @param suohuanjibing
   * @param hosid
   * @param hunyushi
   * @param yuejingshi
   * @param waishangshi
   * @param shoushushi
   * @param qita
   * @returns {Promise<void>}
   */
  async illness({
                  ddid,
                  name,
                  binganhao,
                  qhmm,
                  zhusu,
                  guominshi,
                  jiazushi,
                  jiwangshi,
                  suohuanjibing,
                  hosid,
                  hunyushi,
                  yuejingshi,
                  waishangshi,
                  shoushushi, qita
                }) {
    const {code, msg} = await this.$http('zheer.yygh.apiOrderService.writeIllnessNew', {
      ddid,
      name,
      binganhao,
      qhmm,
      zhusu,
      guominshi,
      jiazushi,
      suohuanjibing,
      hosid,
      orgid: hosid,
      hunyushi,
      yuejingshi,
      waishangshi,
      shoushushi, qita
    })
    if (code !== '0') bus.$emit('msg', msg || 'code' + code);
    return code;
  }

  /**
   * 诊前录入详情
   * @param ddid
   * @returns {Promise<boolean>}
   */
  async illnessDetail(ddid) {
    const {code, msg, succ, ...form} = await this.$http('zheer.yygh.apiOrderService.findIlness', {ddid})
    if (code !== '0') bus.$emit('msg', msg || 'code' + code);
    return code === '0' ? form : {};
  }

  //"appid=wx434c941baaf6f29b|noncestr=dhvpq98x61tgy9izutislbj4big6r4mm|package=prepay_id=wx010937516433126b92e9e9861608378500|timestamp=1572572271|signType=MD5|sign=743E0FFE3AAC81AE74E51D6F13A01820"

  formatPayStr(str) {
    if (typeof str !== 'string') return str;
    let arr = str.split('|'), conf = {};
    arr.forEach(r => {
      let idx = r.indexOf('=');
      let key = r.substring(0, idx);
      let value = r.substring(idx + 1);
      conf[key] = value;
    });
    const res = pick({
      appId: 'appid',
      timeStamp: 'timestamp',
      nonceStr: 'noncestr',
      package: 'package',
      signType: 'signType',
      paySign: 'sign'
    }, conf);
    return res;
  }

  /**
   * 检验是否首诊患者
   * @param compatId
   * @returns {Promise<void>}
   */
  async orderPatientCheck(compatId) {
    const {obj} = await this.$http('nethos.book.order.patient.check', {
      compatId
    });
    return obj;
  }

  /**
   * 是否有医保卡
   * @param idCard
   * @returns {Promise<boolean>}
   */
  async medicalInsurance(idCard) {
    const {obj = false} = await this.$http('nethos.book.medical.insurance', {idCard})
    return obj;
  }

  /**
   * 科室详情
   * @param bookDeptId
   * @returns {Promise<{}>}
   */
  async deptInfo(bookDeptId) {
    const {obj = {}} = await this.$http('nethos.book.dept.info', {
      bookDeptId
    });
    return obj;
  }

  /**
   * 医院详情
   * @param bookHosId
   * @returns {Promise<{}>}
   */
  async hosInfo(bookHosId) {
    const {obj = {}} = await this.$http('nethos.book.hos.info', {
      bookHosId
    });
    return obj;
  }

  /**
   * 科室普通号排班
   * @param bookDeptId
   * @param schemeType
   * @returns {Promise<{}>}
   */
  async docNormalSchemeList(bookDeptId, schemeType) {
    const {obj = {}} = await this.$http('nethos.book.doc.normal.scheme.list', {
      bookDeptId, schemeType
    });
    return obj;
  }

  /**
   * 医生有几个院区坐诊
   * @param bookDocId
   * @returns {Promise<*[]>}
   */
  async bookdocList(bookDocId) {
    const {list = []} = await this.$http('nethos.sys.bookdoc.list', {
      bookDocId
    });
    return list;
  }


  /**
   * mdt
   * @param options.bookHosId
   * @param options.bookDeptId
   * @param options.mdtDeptList
   * @returns {Promise<*[]>}
   */
  async mdt(options) {
    const {obj = {}} = await this.$http("nethos.book.dept.mdt.list", options);
    return obj;
  }

  // 当日挂号取消订单
  async withdrawal(orderId) {
    return await this.$http('nethos.book.order.withdrawal', {orderId})
  }

  // MDT 科室须知
  mdtDeptNotice(bookDeptId) {
    return this.$http('nethos.system.information.dept', {bookDeptId})
  }

  /**
   * 科室是否包含子科室
   * @param options.bookHosId
   * @param options.bookDeptId
   * @returns {Promise<T>}
   */
  async deptHasSubDept(options) {
    const {code, obj} = await this.$http('nethos.book.dept.sub.state.get', options)
    return code === '0' && obj;
  }
}

export const bookService = new BookService();



// WEBPACK FOOTER //
// ./src/services/book.js